import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useToast, callNativeMobileApp, useAppContext } from '@front-activerse/common-components';
import { useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { Button, Spinner } from 'reactstrap';
import createRequest from '../helpers/createRequest';
import createRequestForSaveUserData from '../helpers/createRequestForSaveUserData';
import { dietSettings } from '../types';
import { useTranslator } from '@front-activerse/common-components';
import useSteps from '../StepContext/useSteps';

const ConfirmButton: React.FC = () => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const { values, resetForm, initialValues } = useFormikContext<dietSettings>();
  const [loading, setLoading] = useState(false);
  const { generateDiet, saveUserData, currentMainStep, dietTypes, dietLevels, isConsent } =
    useSteps();
  const navigate = useNavigate();
  const client = useApolloClient();

  const {
    consts: { SLIDE_ROUTES_DURATION, APP_BRAND },
  } = useAppContext();

  const scrollToTopAndNavigate = (url: string) => {
    const el = document.querySelector('.layout') as HTMLInputElement;
    if (el.scrollTop) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate(url);
      }, Number(SLIDE_ROUTES_DURATION));
    } else {
      navigate(url);
    }
  };

  const handleOnClick = async () => {
    setLoading(true);

    // save user data only if diet isn't generated yet, otherwise generate diet
    if (currentMainStep === 'biometric' && !initialValues.dietType) {
      try {
        const response: any = await saveUserData(createRequestForSaveUserData(values));
        if (response.__typename === 'BasicMutationSuccess') {
          resetForm();
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'diet_settings',
            },
          });
          client.refetchQueries({ include: ['DietSettings'] });
          localStorage.setItem('trackerEnabled', 'true');
          scrollToTopAndNavigate(`/diet-settings/${currentMainStep}/loader`);
        }
      } catch (error) {
        toastError();
      } finally {
        setLoading(false);
      }
    } else {
      if (!isConsent) {
        scrollToTopAndNavigate(`/diet-settings/${currentMainStep}/acceptance`);
      } else {
        try {
          const response: any = await generateDiet(
            createRequest(values, dietTypes, dietLevels, String(APP_BRAND))
          );
          if (response.__typename === 'BasicMutationSuccess') {
            resetForm();
            callNativeMobileApp({
              action: 'refresh_data',
              payload: {
                module: 'diet_settings',
              },
            });
            client.refetchQueries({ include: ['DietSettings'] });
            localStorage.setItem('trackerEnabled', 'true');
            scrollToTopAndNavigate(`/diet-settings/${currentMainStep}/loader`);
          }
        } catch (error) {
          toastError();
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <Button
      color="primary"
      className="w-100"
      type="button"
      onClick={handleOnClick}
      disabled={loading}
    >
      {loading ? (
        <>
          <Spinner size="sm" className="me-2">
            Loading...
          </Spinner>
          {t('goalSettings.summary.processingButton')}
        </>
      ) : !isConsent ? (
        t('goalSettings.nextButton')
      ) : (
        t('goalSettings.summary.saveButton')
      )}
    </Button>
  );
};

export default ConfirmButton;
